if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.isCarousel = this.dataset.carousel === 'true';

        if (!this.elements.thumbnails) {
          if (this.isCarousel) {
            this.initCarouselViewerNav();
            this.initCarouselSwipe();
            this.initCarouselKeyboard();
            this.updateCarouselNavButtons();
          }
          return;
        }

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.isCarousel || (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches)) {
          this.removeListSemantic();
        }
        if (this.isCarousel) {
          this.initCarouselViewerNav();
          this.initCarouselSwipe();
          this.initCarouselKeyboard();
          this.updateCarouselNavButtons();
        }
      }

      disconnectedCallback() {
        if (this.boundCarouselKeyDown) {
          document.removeEventListener('keydown', this.boundCarouselKeyDown);
        }
      }

      initCarouselViewerNav() {
        const prevButton = this.elements.viewer?.querySelector('.slider-button--prev');
        const nextButton = this.elements.viewer?.querySelector('.slider-button--next');
        if (!prevButton || !nextButton || prevButton.dataset.carouselNavBound) return;

        const onNavClick = (direction) => (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (prevButton.hasAttribute('disabled') && direction < 0) return;
          if (nextButton.hasAttribute('disabled') && direction > 0) return;
          this.setAdjacentMedia(direction);
        };

        prevButton.addEventListener('click', onNavClick(-1));
        nextButton.addEventListener('click', onNavClick(1));
        prevButton.dataset.carouselNavBound = 'true';
        nextButton.dataset.carouselNavBound = 'true';
        this.updateCarouselNavButtons();
      }

      initCarouselSwipe() {
        const mediaList = this.elements.viewer?.querySelector('.product__media-list');
        if (!mediaList) return;

        const swipeThreshold = 48;
        let startX = 0;
        let startY = 0;
        let tracking = false;

        mediaList.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length !== 1) return;
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
            tracking = true;
          },
          { passive: true }
        );

        mediaList.addEventListener(
          'touchend',
          (event) => {
            if (!tracking) return;
            tracking = false;
            const touch = event.changedTouches[0];
            const deltaX = touch.clientX - startX;
            const deltaY = touch.clientY - startY;
            if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) < Math.abs(deltaY)) return;
            this.setAdjacentMedia(deltaX > 0 ? -1 : 1);
          },
          { passive: true }
        );

        mediaList.addEventListener(
          'touchcancel',
          () => {
            tracking = false;
          },
          { passive: true }
        );

        let pointerStartX = 0;
        let pointerTracking = false;

        mediaList.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          pointerStartX = event.clientX;
          pointerTracking = true;
          mediaList.classList.add('is-dragging');
        });

        mediaList.addEventListener('pointerup', (event) => {
          if (!pointerTracking) return;
          pointerTracking = false;
          mediaList.classList.remove('is-dragging');
          const deltaX = event.clientX - pointerStartX;
          if (Math.abs(deltaX) < swipeThreshold) return;
          this.setAdjacentMedia(deltaX > 0 ? -1 : 1);
        });

        mediaList.addEventListener('pointercancel', () => {
          pointerTracking = false;
          mediaList.classList.remove('is-dragging');
        });
      }

      initCarouselKeyboard() {
        this.setAttribute('tabindex', '-1');
        this.boundCarouselKeyDown = (event) => {
          if (!this.isCarousel) return;
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          if (event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
          if (!this.isCarouselInView()) return;

          event.preventDefault();
          this.setAdjacentMedia(event.key === 'ArrowLeft' ? -1 : 1);
        };
        document.addEventListener('keydown', this.boundCarouselKeyDown);
      }

      isCarouselInView() {
        const rect = this.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom > 0;
      }

      getVisibleMediaSlides() {
        return Array.from(
          this.elements.viewer.querySelectorAll('.product__media-item:not(.product__media-item--variant)')
        );
      }

      getActiveSlideIndex(slides) {
        const activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
        return activeIndex === -1 ? 0 : activeIndex;
      }

      setAdjacentMedia(direction) {
        const slides = this.getVisibleMediaSlides();
        if (slides.length < 2) return;

        const activeIndex = this.getActiveSlideIndex(slides);
        const nextIndex = activeIndex + direction;
        if (nextIndex < 0 || nextIndex >= slides.length) return;
        this.setActiveMedia(slides[nextIndex].dataset.mediaId, false);
      }

      updateCarouselNavButtons() {
        if (!this.isCarousel) return;
        const prevButton = this.elements.viewer?.querySelector('.slider-button--prev');
        const nextButton = this.elements.viewer?.querySelector('.slider-button--next');
        if (!prevButton || !nextButton) return;

        const slides = this.getVisibleMediaSlides();
        if (slides.length < 2) {
          prevButton.setAttribute('disabled', 'disabled');
          nextButton.setAttribute('disabled', 'disabled');
          return;
        }

        const activeIndex = this.getActiveSlideIndex(slides);

        if (activeIndex <= 0) {
          prevButton.setAttribute('disabled', 'disabled');
        } else {
          prevButton.removeAttribute('disabled');
        }

        if (activeIndex >= slides.length - 1) {
          nextButton.setAttribute('disabled', 'disabled');
        } else {
          nextButton.removeAttribute('disabled');
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) {
          this.updateCarouselNavButtons();
          return;
        }
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        this.updateCarouselNavButtons();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
